import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Checkbox,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import MedicationIcon from "@mui/icons-material/Medication";
import { RemoveCircleOutlineRounded } from "@mui/icons-material";
import FlagIcon from "@mui/icons-material/Flag";
import EpicFlagsDialog from "../dialogs/EpicFlagsDialog";

const PrescriptionItemsTable = ({
  t,
  prescriptionAssign,
  searchedRxItems,
  onRxChange,
  onQtyChange,
  onAmountDueChange,
  onControlledSubstanceChange,
  onConsultChange,
  onRemoveItem,
}) => {
  const [flagsDialogOpen, setFlagsDialogOpen] = useState(false);
  const [selectedItemFlags, setSelectedItemFlags] = useState(null);

  const handleCloseFlagsDialog = () => {
    setFlagsDialogOpen(false);
    setSelectedItemFlags(null);
  };

  const hasEpicFlags = (item) => {
    if (!item) return false;
    const hasPrescriptionFlags = item.PrescriptionFlags && Array.isArray(item.PrescriptionFlags) && item.PrescriptionFlags.length > 0;
    const hasFlags = item.Flags && Array.isArray(item.Flags) && item.Flags.length > 0;
    const hasCannotDispenseReasons = item.CannotDispenseReasons && Array.isArray(item.CannotDispenseReasons) && item.CannotDispenseReasons.length > 0;
    return hasPrescriptionFlags || hasFlags || hasCannotDispenseReasons;
  };

  const handleOpenFlagsDialog = (item) => {
    setSelectedItemFlags(item);
    setFlagsDialogOpen(true);
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 3,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          pb: 2,
          borderBottom: "2px solid #f1f5f9",
        }}
      >
        <MedicationIcon
          sx={{
            color: "#7c3aed",
            fontSize: 28,
            mr: 1.5,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: "1.1rem",
            color: "#1e293b",
            letterSpacing: "0.01em",
          }}
        >
          {t("Report.Assign.AssignedItemsTitle")}
        </Typography>
        <Chip
          label={t("Assign.CartLineItemCount", {
            count: searchedRxItems ? searchedRxItems.length : 0,
            defaultValue: "{{count}} in cart",
          })}
          size="medium"
          sx={{
            ml: 2,
            fontWeight: 700,
            fontSize: "0.875rem",
            backgroundColor: "#ede9fe",
            color: "#1e293b",
            border: "1px solid #c4b5fd",
            "& .MuiChip-label": {
              px: 1.5,
            },
          }}
        />
      </Box>
      <Box
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          "&::-webkit-scrollbar": {
            height: "10px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f5f9",
            borderRadius: "5px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#7c3aed",
            borderRadius: "5px",
            border: "2px solid #f1f5f9",
            "&:hover": {
              background: "#6d28d9",
            },
          },
        }}
      >
        <Table size="small" sx={{ minWidth: 620 }}>
          <TableHead>
            <TableRow
              sx={{
                background: "#f1f5f9",
                "& .MuiTableCell-head": {
                  color: "#1e293b",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  borderBottom: "2px solid #cbd5e1",
                  py: 1.25,
                  px: 1,
                },
              }}
            >
              <TableCell align="center" sx={{ px: 0.75, width: "56px" }}></TableCell>
              <TableCell>{t("Assign.ManualConsult.RxHash")}</TableCell>
              <TableCell>{t("Assign.FillID")}</TableCell>
              <TableCell>{t("Assign.PrescriptionID")}</TableCell>
              <TableCell>{t("Assign.DrugName")}</TableCell>
              <TableCell align="center">{t("Assign.Qty")}</TableCell>
              <TableCell align="center">Controlled Substance</TableCell>
              <TableCell align="center">{t("Assign.ConsultRequired")}</TableCell>
              <TableCell align="center">{t("Assign.Flags") || "Flags"}</TableCell>
              <TableCell>{t("Assign.AmountDue")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {searchedRxItems && searchedRxItems.length > 0 ? (
              searchedRxItems.map((item, index) => (
                <TableRow
                  key={index}
                  sx={{
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#f8fafc",
                      transform: "scale(1.01)",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                    },
                    "&:last-child td": {
                      borderBottom: "none",
                    },
                    "& td": {
                      borderColor: "#e2e8f0",
                    },
                  }}
                >
                  <TableCell align="center" sx={{ py: 1.25, px: 0.75, width: "56px" }}>
                    <Tooltip title={t("Assign.RemovePrescription") || "Remove prescription"}>
                      <IconButton
                        onClick={() => onRemoveItem && onRemoveItem(index)}
                        color="primary"
                        size="small"
                        disabled={!prescriptionAssign}
                        sx={{
                          color: "#ef4444",
                          padding: "6px",
                          "&:hover": {
                            backgroundColor: "rgba(239, 68, 68, 0.1)",
                            color: "#dc2626",
                          },
                          "&:disabled": {
                            color: "#cbd5e1",
                          },
                        }}
                      >
                        <RemoveCircleOutlineRounded />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ py: 1.25, px: 1 }}>
                    <TextField
                      size="small"
                      value={item.rxNo || item.rx_no || ""}
                      onChange={(e) =>
                        onRxChange && onRxChange(index, "rxNo", e.target.value)
                      }
                      disabled={!prescriptionAssign}
                      variant="outlined"
                      sx={{ minWidth: 95 }}
                      inputProps={{
                        style: {
                          fontSize: "0.875rem",
                          padding: "7px 10px",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1.25, px: 1 }}>
                    <TextField
                      size="small"
                      value={item.fillNumber || item.fillnumber || ""}
                      disabled
                      variant="outlined"
                      sx={{ minWidth: 65 }}
                      inputProps={{
                        style: {
                          fontSize: "0.875rem",
                          padding: "7px 10px",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1.25, px: 1 }}>
                    <TextField
                      size="small"
                      value={
                        item.prescriptionNumber || item.prescriptionnumber || ""
                      }
                      disabled
                      variant="outlined"
                      sx={{ minWidth: 95 }}
                      inputProps={{
                        style: {
                          fontSize: "0.875rem",
                          padding: "7px 10px",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1.25, px: 1, minWidth: 140 }}>
                    <TextField
                      size="small"
                      value={item.name || ""}
                      disabled
                      variant="outlined"
                      fullWidth
                      inputProps={{
                        style: {
                          fontSize: "0.875rem",
                          padding: "7px 10px",
                          color: "#1e293b",
                          fontWeight: 500,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.25, px: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={item.qty || ""}
                      onChange={(e) =>
                        onQtyChange && onQtyChange(index, e.target.value)
                      }
                      disabled={!prescriptionAssign}
                      variant="outlined"
                      sx={{ width: 65 }}
                      inputProps={{
                        style: {
                          fontSize: "0.875rem",
                          padding: "7px 10px",
                          textAlign: "center",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.25, px: 1 }}>
                    <Checkbox
                      checked={item.is_controlled_substance || false}
                      onChange={(e) =>
                        onControlledSubstanceChange &&
                        onControlledSubstanceChange(index, e.target.checked)
                      }
                      disabled={!prescriptionAssign}
                      size="small"
                      color="primary"
                      sx={{ padding: "5px" }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.25, px: 1 }}>
                    <Checkbox
                      checked={
                        item.ConsultRequire ||
                        item.is_consultrequire ||
                        false
                      }
                      onChange={(e) =>
                        onConsultChange && onConsultChange(index, e.target.checked)
                      }
                      disabled={!prescriptionAssign}
                      size="small"
                      color="primary"
                      sx={{ padding: "5px" }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.25, px: 1 }}>
                    {hasEpicFlags(item) ? (
                      <Tooltip title={t("Assign.ViewEpicFlags") || "View Epic Flags"}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenFlagsDialog(item)}
                          sx={{
                            color: "#dc2626",
                            "&:hover": {
                              backgroundColor: "rgba(220, 38, 38, 0.1)",
                            },
                            p: 0.5,
                          }}
                        >
                          <FlagIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography
                        sx={{
                          color: "#cbd5e1",
                          fontSize: "0.75rem",
                          fontStyle: "italic",
                        }}
                      >
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <TextField
                      size="small"
                      type="text"
                      value={item.CoPayAmount || item.amount_due || ""}
                      onChange={(e) =>
                        onAmountDueChange &&
                        onAmountDueChange(index, e.target.value)
                      }
                      disabled={!prescriptionAssign}
                      variant="outlined"
                      sx={{ width: 95 }}
                      InputProps={{
                        startAdornment: (
                          <Box
                            component="span"
                            sx={{
                              mr: 0.5,
                              color: "text.secondary",
                              fontSize: "0.875rem",
                            }}
                          >
                            $
                          </Box>
                        ),
                        inputProps: {
                          style: {
                            fontSize: "0.875rem",
                            padding: "7px 6px 7px 4px",
                          },
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                  sx={{
                    py: 4,
                    fontSize: "0.875rem",
                    color: "text.secondary",
                  }}
                >
                  {t("Assign.NoDataFound") || "No data found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      
      <EpicFlagsDialog
        open={flagsDialogOpen}
        onClose={handleCloseFlagsDialog}
        item={selectedItemFlags}
        rxNumber={(selectedItemFlags && (selectedItemFlags.prescriptionNumber || selectedItemFlags.rxNo)) || null}
        t={t}
      />
    </Paper>
  );
};

PrescriptionItemsTable.propTypes = {
  t: PropTypes.func.isRequired,
  prescriptionAssign: PropTypes.bool,
  searchedRxItems: PropTypes.array,
  onRxChange: PropTypes.func,
  onQtyChange: PropTypes.func,
  onAmountDueChange: PropTypes.func,
  onControlledSubstanceChange: PropTypes.func,
  onConsultChange: PropTypes.func,
  onRemoveItem: PropTypes.func,
};

PrescriptionItemsTable.defaultProps = {
  prescriptionAssign: true,
  searchedRxItems: [],
  onRxChange: undefined,
  onQtyChange: undefined,
  onAmountDueChange: undefined,
  onControlledSubstanceChange: undefined,
  onConsultChange: undefined,
  onRemoveItem: undefined,
};

export default PrescriptionItemsTable;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/components/PrescriptionItemsTable.jsx